/**
 * @file     visitorSignals.ts
 * @purpose  Browser-side visitor signals for the prompt system: hasApp,
 *           isFirstTimeBuyer (one resolver over every first-time-buyer
 *           signal) and hasBookedCall. Signed-in facts come from
 *           GET /api/prompts/signals, fetched once per session and only
 *           after the prompt timing gate passes; the cache lives in
 *           sessionStorage.
 */
'use client';

import {
  PROMPT_SIGNALS_SESSION_KEY,
  PROMPT_STORAGE_KEY,
  isFtbPage,
  type HasAppSignal,
} from '@/core/config/prompts';
import { getDataPoint } from '@/services/visitor-profile';
import { UTM_FIRST_TOUCH_COOKIE, parseJsonCookie } from '@/services/analytics/attributionCookies';
import { inferFromCampaign } from '@/features/deal-finder/intent/campaignMap';
import { inferFromKeyword } from '@/features/deal-finder/intent/keywordMap';
import { listSessions } from '@/features/property-finder/sessionHistory';
import type { ServerVisitorSignals } from './visitorSignalsServer';

export const SIGNALS_ENDPOINT = '/api/prompts/signals';

export type FtbSignal =
  | 'ftb_page'
  | 'visitor_profile'
  | 'property_finder_door'
  | 'utm_persona'
  | 'academy_experience'
  | 'preferred_lens'
  | 'rent_vs_buy'
  | 'readiness';

/** Every input the first-time-buyer resolver reads. Missing means "no signal". */
export interface FtbFacts {
  pathname?: string | null;
  /** visitor-profile buyerType, local or from the account. */
  buyerType?: string | null;
  propertyFinderDoors?: readonly string[];
  utmPersona?: string | null;
  academyExperienceLevel?: string | null;
  preferredLens?: string | null;
  /** rent-vs-buy situation; 'renting' means renting and thinking about buying. */
  rentVsBuySituation?: string | null;
  /** readiness BuyerTrackData.firstTimeBuyer (not written anywhere yet). */
  readinessFirstTimeBuyer?: boolean | null;
}

/** The first signal that marks a first-time buyer, in a fixed order, or null. */
export function firstTimeBuyerSignal(facts: FtbFacts): FtbSignal | null {
  if (facts.pathname && isFtbPage(facts.pathname)) return 'ftb_page';
  if (facts.buyerType === 'first-time') return 'visitor_profile';
  if (facts.propertyFinderDoors?.includes('first-time')) return 'property_finder_door';
  if (facts.utmPersona === 'first-time') return 'utm_persona';
  if (facts.academyExperienceLevel === 'first-time') return 'academy_experience';
  if (facts.preferredLens === 'first_time_buyer') return 'preferred_lens';
  if (facts.rentVsBuySituation === 'renting') return 'rent_vs_buy';
  if (facts.readinessFirstTimeBuyer === true) return 'readiness';
  return null;
}

export function isFirstTimeBuyer(facts: FtbFacts): boolean {
  return firstTimeBuyerSignal(facts) != null;
}

function readCookie(name: string): string | null {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit ? hit.slice(name.length + 1) : null;
}

/** The ad persona from the first-touch UTM cookie, resolved the way the deal-finder does. */
export function readUtmPersona(): string | null {
  const utm = parseJsonCookie<Record<string, string>>(readCookie(UTM_FIRST_TOUCH_COOKIE));
  if (!utm) return null;
  return inferFromCampaign(utm.utm_campaign, utm.utm_content).persona ?? inferFromKeyword(utm.utm_term).persona ?? null;
}

function dataPointString(key: string): string | null {
  const value = getDataPoint(key)?.value;
  return typeof value === 'string' ? value : null;
}

/** First-time-buyer facts this browser holds, merged with the account's when signed in. */
export function readClientFtbFacts(pathname: string | null, server?: ServerVisitorSignals['ftb'] | null): FtbFacts {
  return {
    pathname,
    buyerType: dataPointString('buyerType') ?? server?.profileBuyerType ?? null,
    propertyFinderDoors: listSessions().map((s) => s.doorId),
    utmPersona: readUtmPersona(),
    academyExperienceLevel: server?.academyExperienceLevel ?? null,
    preferredLens: server?.preferredLens ?? null,
    rentVsBuySituation: dataPointString('rentVsBuySituation'),
    readinessFirstTimeBuyer: null,
  };
}

/** Signed in: the account's answer. Anonymous: the web cannot know. */
export function hasApp(signals: Pick<ServerVisitorSignals, 'hasApp'> | null, signedIn: boolean): HasAppSignal {
  if (!signedIn || !signals) return 'unknown';
  return signals.hasApp;
}

/** A prior acceptance or completed booking of the call offer, kept in prompt storage. */
export function hasStoredCallAcceptance(): boolean {
  try {
    const raw = window.localStorage.getItem(PROMPT_STORAGE_KEY);
    if (!raw) return false;
    const record = (JSON.parse(raw) as { byId?: Record<string, { acceptedAt?: number; completedAt?: number }> }).byId
      ?.ftb_call_offer;
    return Boolean(record?.acceptedAt || record?.completedAt);
  } catch {
    // Unreadable storage: no stored acceptance to honour.
    return false;
  }
}

export function hasBookedCall(signals: Pick<ServerVisitorSignals, 'hasBookedCall'> | null): boolean {
  return signals?.hasBookedCall === true || hasStoredCallAcceptance();
}

interface CachedSignals {
  userId: string;
  signals: ServerVisitorSignals;
}

function readCache(userId: string): ServerVisitorSignals | null {
  try {
    const raw = window.sessionStorage.getItem(PROMPT_SIGNALS_SESSION_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedSignals;
    return cached.userId === userId ? cached.signals : null;
  } catch {
    // Unreadable cache: fetch again.
    return null;
  }
}

function writeCache(userId: string, signals: ServerVisitorSignals): void {
  try {
    window.sessionStorage.setItem(PROMPT_SIGNALS_SESSION_KEY, JSON.stringify({ userId, signals } satisfies CachedSignals));
  } catch {
    // sessionStorage blocked: the next page fetches again.
  }
}

const inflight = new Map<string, Promise<ServerVisitorSignals | null>>();

/**
 * The signed-in visitor's server signals, once per session. Null when the
 * request fails; callers then treat hasApp as unknown and never show the
 * app prompt.
 */
export function fetchVisitorSignals(userId: string): Promise<ServerVisitorSignals | null> {
  const cached = readCache(userId);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(userId);
  if (pending) return pending;
  const request = fetch(SIGNALS_ENDPOINT, { credentials: 'same-origin', cache: 'no-store' })
    .then(async (res) => {
      if (!res.ok) return null;
      const signals = (await res.json()) as ServerVisitorSignals;
      writeCache(userId, signals);
      return signals;
    })
    .catch(() => null)
    .finally(() => inflight.delete(userId));
  inflight.set(userId, request);
  return request;
}
